import { err, ok } from "../result.js";
import { withDiscriminant } from "../variant.js";
import { collectDeclEdges } from "./edges.js";
import { validate } from "./validate.js";
import { PRIMITIVES, } from "./types.js";
/** Build a TypeRef. Resolution is deferred to validate(). */
export function ref(name, args = []) {
    return { name, args, resolution: { kind: "external" } };
}
export function record(name, fields, generics = []) {
    return { kind: "record", name, generics, fields: fields.map(toField) };
}
export function union(name, variants, generics = [], spec) {
    return {
        kind: "union",
        name,
        generics,
        ...(spec?.untagged === true ? { untagged: true } : {}),
        variants: variants.map(toVariant),
    };
}
export function alias(name, target, generics = []) {
    return { kind: "alias", name, generics, target };
}
function toField(f) {
    return { name: f.name, type: f.type };
}
function toVariant(v) {
    return withDiscriminant({
        name: v.name,
        fields: (v.fields ?? []).map(toField),
    }, v.discriminant);
}
export class ModelBuilder {
    decls = [];
    add(decl) {
        this.decls.push(decl);
        return this;
    }
    build() {
        const draft = {
            decls: this.decls,
            edges: [],
            externals: [],
        };
        const resolved = resolveResolutions(draft);
        const diagnostics = validate(resolved);
        const errs = diagnostics.filter((d) => d.severity === "error");
        return errs.length === 0 ? ok(resolved) : err(diagnostics);
    }
    buildPartial() {
        const draft = { decls: this.decls, edges: [], externals: [] };
        const resolved = resolveResolutions(draft);
        return { model: resolved, diagnostics: validate(resolved) };
    }
}
/** Walk a programmatically-built Model and fill in `resolution` and `edges` correctly. */
export function resolveResolutions(model) {
    const declNames = new Map();
    for (const d of model.decls) {
        declNames.set(d.name, d);
    }
    const externals = new Set();
    const fixRef = (t, generics, owner) => {
        // Declared names win over PRIMITIVES — see resolveTypeRef in build.ts. [MODEL-SCALARS]
        let resolution;
        if (generics.has(t.name)) {
            resolution = { kind: "typeParam", owner };
        }
        else if (declNames.has(t.name)) {
            resolution = { kind: "declared", declName: t.name };
        }
        else if (PRIMITIVES.has(t.name)) {
            resolution = { kind: "primitive" };
        }
        else {
            externals.add(t.name);
            resolution = { kind: "external" };
        }
        return {
            name: t.name,
            args: t.args.map((a) => fixRef(a, generics, owner)),
            resolution,
        };
    };
    const newDecls = model.decls.map((d) => {
        const generics = new Set(d.generics);
        if (d.kind === "record") {
            return {
                ...d,
                fields: d.fields.map((f) => ({ name: f.name, type: fixRef(f.type, generics, d.name) })),
            };
        }
        if (d.kind === "union") {
            return {
                ...d,
                variants: d.variants.map((v) => withDiscriminant({
                    name: v.name,
                    fields: v.fields.map((f) => ({ name: f.name, type: fixRef(f.type, generics, d.name) })),
                }, v.discriminant)),
            };
        }
        return { ...d, target: fixRef(d.target, generics, d.name) };
    });
    // rebuild edges from the resolved decls
    const out = { decls: newDecls, edges: [], externals: [...externals].sort() };
    out.edges = collectDeclEdges(out.decls);
    return out;
}
//# sourceMappingURL=builder.js.map